import { Injectable, Directive } from '@angular/core';
import { AppConfigService } from '../app.config';
import { AccountService } from '../entry/account.service';
import { ConstantService } from '../lib/common/constant.service';
import { LOCALSTORAGE_KEY_USER_PREFERENCE } from '../lib/common/common.data';
import { HelperLib } from '../lib/common/helper.lib';
import { DashboardView, UserPreferenceEventLogInfo, UserPreferenceGlobalInfo, UserPreferenceHomeInfo, UserPreferenceInfo, UserPreferenceLicenseInfo } from './user-preference.data';

@Directive()
@Injectable()
export class UserPreferenceService {
    _userPreferenceMap: { [accountName: string]: UserPreferenceInfo };

    get userPreference(): UserPreferenceInfo {
        return this._userPreferenceMap[this.accountSvc.accountName];
    }

    constructor(private accountSvc: AccountService, private constantSvc: ConstantService) {
        this.accountSvc.loginChanged.subscribe((isLogin: boolean) => {
            if (isLogin) {
                this.init();
            }
        });

        this.init();
    }

    private init(): void {
        this.getUserPrefereceFromCache();

        this._userPreferenceMap[this.accountSvc.accountName].global = this._userPreferenceMap[this.accountSvc.accountName].global || new UserPreferenceGlobalInfo();
        this._userPreferenceMap[this.accountSvc.accountName].global.theme = this._userPreferenceMap[this.accountSvc.accountName].global.theme || 'standard';
        this._userPreferenceMap[this.accountSvc.accountName].home = this._userPreferenceMap[this.accountSvc.accountName].home || new UserPreferenceHomeInfo();
        this._userPreferenceMap[this.accountSvc.accountName].home.overviewLayout = this._userPreferenceMap[this.accountSvc.accountName].home.overviewLayout || AppConfigService.configs.preference.home.overviewLayout;
        this._userPreferenceMap[this.accountSvc.accountName].home.device = this._userPreferenceMap[this.accountSvc.accountName].home.device || {};
        this._userPreferenceMap[this.accountSvc.accountName].home.device.view = this._userPreferenceMap[this.accountSvc.accountName].home.device.view || DashboardView.table;
        this._userPreferenceMap[this.accountSvc.accountName].home.device.pageCapacity = this._userPreferenceMap[this.accountSvc.accountName].home.device.pageCapacity || 30;
        this._userPreferenceMap[this.accountSvc.accountName].home.device.tableLayoutColumnOptionMap = this._userPreferenceMap[this.accountSvc.accountName].home.device.tableLayoutColumnOptionMap || {};
        this._userPreferenceMap[this.accountSvc.accountName].home.device.searchOptions = this._userPreferenceMap[this.accountSvc.accountName].home.device.searchOptions || {};
        this._userPreferenceMap[this.accountSvc.accountName].home.device.showSubDevGroupPopupHint = this._userPreferenceMap[this.accountSvc.accountName].home.device.showSubDevGroupPopupHint ?? true;

        this._userPreferenceMap[this.accountSvc.accountName].eventLog = this._userPreferenceMap[this.accountSvc.accountName].eventLog || new UserPreferenceEventLogInfo();
        this._userPreferenceMap[this.accountSvc.accountName].eventLog.filter = this._userPreferenceMap[this.accountSvc.accountName].eventLog.filter || {
            level: {},
            time: {},
            event: {}
        };
        this._userPreferenceMap[this.accountSvc.accountName].license = this._userPreferenceMap[this.accountSvc.accountName].license || new UserPreferenceLicenseInfo();
        this._userPreferenceMap[this.accountSvc.accountName].license.filter = this._userPreferenceMap[this.accountSvc.accountName].license.filter || {
            licenseType: {},
            expiryDate: {}
        };
    }

    private getUserPrefereceFromCache(): void {
        //get user preference from localstorage
        const p_raw: string = HelperLib.getLocalStorageRecord(LOCALSTORAGE_KEY_USER_PREFERENCE);
        try {
            this._userPreferenceMap = JSON.parse(p_raw);
        }
        catch (ex) {
        }

        this._userPreferenceMap = this._userPreferenceMap || {};
        this._userPreferenceMap[this.accountSvc.accountName] = this._userPreferenceMap[this.accountSvc.accountName] || new UserPreferenceInfo();
    }

    changeGlobalTheme(themeName: string): void {
        if (this._userPreferenceMap[this.accountSvc.accountName].global.theme !== themeName) {
            this._userPreferenceMap[this.accountSvc.accountName].global.theme = themeName;
            this.setUserPreference();
        }
    }

    changeHomeLayout(group: boolean): void {
        const targetLayout: string = group ? 'group' : 'device';
        if (this._userPreferenceMap[this.accountSvc.accountName].home.overviewLayout !== targetLayout) {
            this._userPreferenceMap[this.accountSvc.accountName].home.overviewLayout = targetLayout;
            this.setUserPreference();
        }
    }

    changeHomeDeviceView(view: DashboardView): void {
        if (this._userPreferenceMap[this.accountSvc.accountName].home.device.view !== view) {
            this._userPreferenceMap[this.accountSvc.accountName].home.device.view = view;
            this.setUserPreference();
        }
    }

    changeHomeDevicePageCapacity(c: number): void {
        if (this._userPreferenceMap[this.accountSvc.accountName].home.device.pageCapacity !== c) {
            this._userPreferenceMap[this.accountSvc.accountName].home.device.pageCapacity = c;
            this.setUserPreference();
        }
    }

    changeHomeShowDevSubGroupPopoupHint(show: boolean): void {
        if (this._userPreferenceMap[this.accountSvc.accountName].home.device.showSubDevGroupPopupHint !== show) {
            this._userPreferenceMap[this.accountSvc.accountName].home.device.showSubDevGroupPopupHint = show;
            this.setUserPreference();
        }
    }

    changeHomeTableViewColumns(cols: { key: string, show: boolean }[]): void {
        this._userPreferenceMap[this.accountSvc.accountName].home.device.tableLayoutColumnOptionMap = cols.reduce((prev, curr) => {
            prev[curr.key] = curr.show;
            return prev;
        }, {});
        this.setUserPreference();
    }

    changeHomeDeviceSearchOption(options: { key: string, enabled: boolean }[]): void {
        this._userPreferenceMap[this.accountSvc.accountName].home.device.searchOptions = options.reduce((prev, curr) => {
            prev[curr.key] = curr.enabled;
            return prev;
        }, {});
        this.setUserPreference();
    }

    changeEventLogFilter(
        level: { [levelName: string]: boolean },
        time: { beginDate: string, beginTime: string, endDate: string, endTime: string },
        event: { [categoryName: string]: { [eventName: string]: boolean } }
    ): void {
        this._userPreferenceMap[this.accountSvc.accountName].eventLog.filter = {
            level: level,
            time: time,
            event: event
        };
        this.setUserPreference();
    }

    changeLicenseFilter(type: { [type: string]: boolean }, expiryDate: { beginDate: string, endDate: string }): void {
        this._userPreferenceMap[this.accountSvc.accountName].license.filter = {
            licenseType: type,
            expiryDate: expiryDate
        };
        this.setUserPreference();
    }

    private setUserPreference(): void {
        HelperLib.setLocalStorageRecord(LOCALSTORAGE_KEY_USER_PREFERENCE, JSON.stringify(this._userPreferenceMap));
    }
}